import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, Trash2, Power, ExternalLink, X } from "lucide-react";

const portalUrl = (token) => `${window.location.origin}/sms/${token}`;

function OriginatorInput({ values, onChange, disabled }) {
  const [text, setText] = useState("");
  const addOne = (v) => {
    const s = (v || "").trim().toUpperCase().slice(0, 11);
    if (!s) return;
    if (values.includes(s)) { setText(""); return; }
    onChange([...values, s]);
    setText("");
  };
  const remove = (s) => onChange(values.filter((v) => v !== s));
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addOne(text);
    } else if (e.key === "Backspace" && !text && values.length) {
      onChange(values.slice(0, -1));
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-turq-50 border border-turq-200 text-turq-800 rounded-lg text-xs font-mono font-semibold" data-testid={`originator-chip-${v}`}>
            {v}
            {!disabled && (
              <button type="button" onClick={() => remove(v)} className="hover:text-red-600" data-testid={`remove-originator-${v}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => addOne(text)}
          disabled={disabled}
          maxLength={11}
          placeholder="ETELEKOM · Enter veya virgül ile ekle (maks 11 karakter)"
          data-testid="originator-input"
          className="input-soft flex-1 font-mono uppercase"
        />
        <button type="button" onClick={() => addOne(text)} disabled={disabled || !text.trim()} className="px-3 text-xs font-semibold border border-turq-200 text-turq-800 rounded-lg hover:bg-turq-50 disabled:opacity-40">+ Ekle</button>
      </div>
      {values.length === 0 && <div className="text-[11px] text-amber-600 mt-1.5">En az bir originator gerekli.</div>}
      {values.length > 0 && <div className="text-[11px] text-slate-500 mt-1.5">Müşteri portal&apos;dan birini seçerek gönderim yapabilir. <strong className="text-turq-800">{values[0]}</strong> varsayılan.</div>}
    </div>
  );
}

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", originators: [], sms_username: "", sms_password: "", portal_username: "", portal_password: "", note: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/customers");
      setList(data);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", originators: [], sms_username: "", sms_password: "", portal_username: "", portal_password: "", note: "" });
    setOpen(true);
  };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      originators: c.originators || [c.originator].filter(Boolean),
      sms_username: c.sms_username,
      sms_password: "",
      portal_username: c.portal_username || "",
      portal_password: "",
      note: c.note || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.originators || form.originators.length === 0) {
      toast.error("En az bir originator girin");
      return;
    }
    try {
      if (editing) {
        const patch = { ...form };
        if (!patch.sms_password) delete patch.sms_password;
        if (!patch.portal_password) delete patch.portal_password;
        if (!patch.portal_username) delete patch.portal_username;
        await api.put(`/admin/customers/${editing.id}`, patch);
        toast.success("Müşteri güncellendi");
      } else {
        await api.post("/admin/customers", form);
        toast.success("Müşteri oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail));
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Kopyalandı");
  };

  const rotate = async (id) => {
    if (!window.confirm("Token yenilensin mi? Eski bağlantı çalışmayacak.")) return;
    await api.post(`/admin/customers/${id}/rotate-token`);
    toast.success("Token yenilendi");
    load();
  };

  const toggleActive = async (c) => {
    await api.put(`/admin/customers/${c.id}`, { active: !c.active });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Müşteri silinsin mi?")) return;
    await api.delete(`/admin/customers/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tiny-label mb-2">Yönetim</div>
          <h1 className="font-display font-black text-4xl text-slate-900">Müşteriler</h1>
        </div>
        <button onClick={startCreate} data-testid="new-customer-btn" className="flex items-center gap-2 btn-primary text-sm font-semibold px-4 py-2.5 rounded-xl">
          <Plus className="w-4 h-4" /> Yeni Müşteri
        </button>
      </div>

      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/50">
            <tr className="text-left border-b border-turq-100">
              <th className="px-5 py-3 tiny-label">Ad</th>
              <th className="px-5 py-3 tiny-label">Originator(ler)</th>
              <th className="px-5 py-3 tiny-label">API Kullanıcı</th>
              <th className="px-5 py-3 tiny-label">Portal Linki</th>
              <th className="px-5 py-3 tiny-label">Durum</th>
              <th className="px-5 py-3 tiny-label">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-400">Henüz müşteri yok. Yeni müşteri oluşturun.</td></tr>
            )}
            {list.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-turq-50/40" data-testid={`customer-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  {c.note && <div className="text-xs text-slate-500 mt-0.5">{c.note}</div>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {(c.originators || [c.originator]).filter(Boolean).map((o) => (
                      <span key={o} className="font-mono text-[11px] bg-turq-50 text-turq-800 px-1.5 py-0.5 rounded">{o}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs">
                  <div className="text-slate-900">{c.sms_username}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {c.portal_username ? (
                      <span>portal: <span className="text-turq-700 font-semibold">@{c.portal_username}</span></span>
                    ) : (
                      <span className="text-amber-600">portal girişi yok</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-[11px] bg-turq-50 text-turq-800 px-2 py-1 rounded-md truncate max-w-[260px]" title={portalUrl(c.token)}>{portalUrl(c.token)}</code>
                    <button onClick={() => copy(portalUrl(c.token))} className="text-slate-500 hover:text-turq-700" title="Kopyala" data-testid={`copy-link-${c.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <a href={portalUrl(c.token)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-turq-700" title="Aç">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => toggleActive(c)} data-testid={`toggle-${c.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                    {c.active ? "Aktif" : "Pasif"}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(c)} className="text-xs px-2.5 py-1 border border-turq-200 text-turq-800 rounded-md hover:bg-turq-50" data-testid={`edit-${c.id}`}>Düzenle</button>
                    <button onClick={() => rotate(c.id)} className="p-1.5 text-slate-500 hover:text-turq-700" title="Token yenile" data-testid={`rotate-${c.id}`}>
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button onClick={() => toggleActive(c)} className="p-1.5 text-slate-500 hover:text-turq-700" title="Aktif/Pasif">
                      <Power className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c.id)} className="p-1.5 text-red-500 hover:text-red-700" title="Sil" data-testid={`delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Slide-over panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-slate-900/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={save} className="w-full max-w-md bg-white border-l border-turq-100 p-8 overflow-y-auto" data-testid="customer-form">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="tiny-label">{editing ? "Düzenle" : "Yeni"}</div>
                <h3 className="font-display font-bold text-2xl text-slate-900 mt-1">Müşteri</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 hover:bg-turq-50 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            {[
              { k: "name", l: "Müşteri Adı", req: true, ph: "ABC Şirketi" },
              { k: "sms_username", l: "API Kullanıcı Adı (E-Telekom)", req: true, ph: "testapi" },
              { k: "sms_password", l: `API Şifresi (E-Telekom)${editing ? " — değiştirmek için doldurun" : ""}`, req: !editing, ph: "••••••", type: "password" },
              { k: "portal_username", l: "Portal Kullanıcı Adı (müşteri girişi)", req: true, ph: "abc-sirket" },
              { k: "portal_password", l: `Portal Şifresi (müşteri girişi)${editing ? " — değiştirmek için doldurun" : ""}`, req: !editing, ph: "••••••", type: "password" },
            ].map((f) => (
              <div key={f.k} className="mb-4">
                <label className="tiny-label block mb-2">{f.l}</label>
                <input
                  type={f.type || "text"}
                  required={f.req}
                  value={form[f.k]}
                  placeholder={f.ph}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`form-${f.k}`}
                  className="input-soft"
                />
              </div>
            ))}

            <div className="mb-4">
              <label className="tiny-label block mb-2">Originator(ler) <span className="text-slate-400 font-normal normal-case">— birden fazla ekleyebilirsiniz</span></label>
              <OriginatorInput
                values={form.originators}
                onChange={(v) => setForm({ ...form, originators: v })}
              />
            </div>
            <div className="mb-6">
              <label className="tiny-label block mb-2">Not (opsiyonel)</label>
              <textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="form-note"
                className="input-soft"
              />
            </div>

            <button type="submit" data-testid="form-save" className="w-full btn-primary text-sm font-semibold py-3 rounded-xl">
              {editing ? "Güncelle" : "Oluştur ve Link Üret"}
            </button>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
